/**
 * ч.1313: вкладка «Відповідальні» в адмініструванні акаунта.
 *
 * Відповідальний за записом — людина, яка його бачить, якщо її роль показує лише власні
 * записи (ч.1312). Тут власник / адміністратор:
 *  1) зіставляє користувачів 1С зі своїми — перенесені документи («Ответственный») і
 *     контрагенти («Основной менеджер покупателя») отримують відповідального;
 *  2) передає всі записи людини іншій (звільнилась, змінила ділянку).
 * Передача окремого запису — у самому документі / картці контрагента.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recordOwners, type OwnerMember, type TransferAllResult } from '@/api/recordOwners';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import { extractApiError } from '@/utils/extractApiError';
import TchTable from '@/shared/components/TchTable';

const memberLabel = (m: OwnerMember) => `${m.full_name || m.email} — ${m.email}`;

export default function OwnersSection() {
  const q = useQuery({ queryKey: ['record-owners-users-1c'], queryFn: () => recordOwners.users1c() });
  const members = q.data?.members ?? [];
  return (
    <div data-testid="owners-section">
      <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0.5rem 0 1rem' }}>
        Відповідальний за документом чи контрагентом — людина, яка його бачить, коли її роль
        показує лише власні записи (вкладка «Ролі»). Новому запису відповідальним стає той, хто
        його створив. Окремий запис передають у самому документі чи картці контрагента — блок
        «Відповідальний».
      </div>
      <Users1CBlock loading={q.isLoading} users={q.data?.users ?? []} members={members} />
      <TransferAllBlock members={members} />
    </div>
  );
}

function Users1CBlock({ loading, users, members }: {
  loading: boolean;
  users: Awaited<ReturnType<typeof recordOwners.users1c>>['users'];
  members: OwnerMember[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const byId = useMemo(() => new Map(members.map((m) => [m.user_id, m])), [members]);
  const mut = useMutation({
    mutationFn: ({ ext, user }: { ext: string; user: string | null }) => recordOwners.map1c(ext, user),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['record-owners-users-1c'] });
      qc.invalidateQueries({ queryKey: ['record-owner'] });
      toast.show({ type: 'success', message: `збережено; відповідального змінено у записах: ${d.updated}` });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  return (
    <section style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ margin: '0 0 0.25rem' }}>Користувачі 1С</h3>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
        У 1С у документі є «Відповідальний», а в контрагента — «Основний менеджер покупця».
        Зіставте користувачів 1С зі своїми — перенесені записи отримають відповідального.
        Записи, які ви вже передали вручну, зіставлення не змінює.
      </div>
      {loading && <div style={{ color: 'var(--muted)' }}>Завантаження…</div>}
      {!loading && users.length === 0 && (
        <div style={{ color: 'var(--muted)' }} data-testid="users-1c-empty">
          Користувачів 1С ще немає. Вони зʼявляться після перенесення з 1С обробкою від
          17.10.2026 або новішою.
        </div>
      )}
      {users.length > 0 && (
        <TchTable data-testid="users-1c-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Користувач 1С</th>
              <th style={{ textAlign: 'right', width: '7rem' }}>Документів</th>
              <th style={{ textAlign: 'right', width: '7rem' }}>Контрагентів</th>
              <th style={{ textAlign: 'left', width: '22rem' }}>Користувач сервісу</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const suggested = !u.mapped_user_id && u.suggested_user_id ? byId.get(u.suggested_user_id) : undefined;
              return (
                <tr key={u.external_id} data-testid={`user-1c-${u.external_id}`}>
                  <td>
                    {u.name || '—'}
                    {u.code && u.code !== u.name && (
                      <span style={{ color: 'var(--muted)', marginLeft: '0.4rem' }}>{u.code}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{u.documents}</td>
                  <td style={{ textAlign: 'right' }}>{u.contractors}</td>
                  <td>
                    <select value={u.mapped_user_id ?? ''} disabled={mut.isPending}
                      aria-label={`Користувач сервісу для «${u.name}»`}
                      onChange={(e) => mut.mutate({ ext: u.external_id, user: e.target.value || null })}>
                      <option value="">— не зіставлено —</option>
                      {members.map((m) => <option key={m.user_id} value={m.user_id}>{memberLabel(m)}</option>)}
                    </select>
                    {suggested && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        схоже, це {suggested.full_name || suggested.email}{' '}
                        <button type="button" className="btn-link" disabled={mut.isPending}
                          onClick={() => mut.mutate({ ext: u.external_id, user: suggested.user_id })}>
                          зіставити
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </TchTable>
      )}
    </section>
  );
}

function TransferAllBlock({ members }: { members: OwnerMember[] }) {
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [preview, setPreview] = useState<TransferAllResult | null>(null);
  const reset = () => setPreview(null);
  const count = useMutation({
    mutationFn: () => recordOwners.transferAll(from, to, true),
    onSuccess: setPreview,
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const run = useMutation({
    mutationFn: () => recordOwners.transferAll(from, to, false),
    onSuccess: (d) => {
      setPreview(null);
      qc.invalidateQueries({ queryKey: ['record-owner'] });
      toast.show({ type: 'success', message: `передано записів: ${d.total}` });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e) }),
  });
  const name = (id: string) => members.find((m) => m.user_id === id)?.full_name || '';
  return (
    <section>
      <h3 style={{ margin: '0 0 0.25rem' }}>Передати всі записи користувача</h3>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.6rem' }}>
        Коли людина звільняється чи змінює ділянку: усі її документи й контрагенти переходять
        іншій. Документи організацій, до яких новий відповідальний не має доступу, лишаються —
        система покаже, скільки їх.
      </div>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label htmlFor="owners-from">Від кого</label>
          <select id="owners-from" value={from} onChange={(e) => { setFrom(e.target.value); reset(); }}>
            <option value="">— оберіть —</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{memberLabel(m)}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label htmlFor="owners-to">Кому</label>
          <select id="owners-to" value={to} onChange={(e) => { setTo(e.target.value); reset(); }}>
            <option value="">— оберіть —</option>
            {members.filter((m) => m.user_id !== from).map((m) => (
              <option key={m.user_id} value={m.user_id}>{memberLabel(m)}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2', alignSelf: 'end' }}>
          <button type="button" disabled={!from || !to || from === to || count.isPending}
            onClick={() => count.mutate()}>порахувати</button>
        </div>
      </div>
      {preview && (
        <div className="mc__panel mc__panel--info" data-testid="transfer-all-preview"
          style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
          {preview.total === 0 ? 'Записів для передачі немає.' : (
            <>
              Буде передано {preview.total}:
              <ul style={{ margin: '0.25rem 0', paddingLeft: '1.2rem' }}>
                {preview.kinds.map((k) => <li key={k.code}>{k.label} — {k.count}</li>)}
              </ul>
            </>
          )}
          {preview.kept > 0 && (
            <div>Лишаться за {name(from)}: {preview.kept} — у організаціях, де {name(to)} доступу не має.</div>
          )}
          {preview.total > 0 && (
            <div className="drawer__actions" style={{ marginTop: '0.5rem' }}>
              <button type="button" className="primary" disabled={run.isPending}
                onClick={() => {
                  confirm({
                    message: `Передати ${preview.total} записів від ${name(from)} до ${name(to)}?`,
                    okLabel: 'передати',
                  }).then((ok) => { if (ok) run.mutate(); });
                }}>
                {run.isPending ? 'передаємо…' : `передати ${preview.total}`}
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
